import { UserData, User, RewardEventRequestBody } from '../types';
import api from './config';

const USER_API_URL = `${process.env.REACT_APP_SERVER_URL}/user`;

/**
 * Function that adds a reward to a user.
 *
 * @param uid - The unique identifier of the user receiving the reward.
 * @param reward - The reward being added to the user.
 * @param type - The type of the reward, either 'award' or 'achievement'.
 * @throws Error Throws an error if the request fails or the response status is not 200.
 */
const addAward = async (uid: string, reward: string): Promise<User[]> => {
  const reqBody: RewardEventRequestBody = {
    uid,
    reward,
    type: 'award',
  };
  const res = await api.put(`${USER_API_URL}/rewardEvent`, reqBody);
  if (res.status !== 200) {
    throw new Error('Error when adding a reward to user');
  }
  return res.data;
};

/*
 * Function to get a user by their username and password.
 *
 * @param username - The username of the user to retrieve.
 * @param password - The password of the user to retrieve.
 * @throws Error if there is an issue fetching the user by username and password.
 */
const loginUser = async (username: string, password: string): Promise<User> => {
  const res = await api.post(`${USER_API_URL}/loginUser`, { username, password });
  if (res.status === 404) {
    throw new Error('Invalid username or password');
  }

  if (res.status !== 200 || !res.data) {
    throw new Error('Error fetching user');
  }

  return res.data;
};

/**
 * Function to create a new user.
 * @param username - The username of the new user.
 * @param password - The password of the new user.
 * @param profilePicture - The profile picture of the new user.
 *
 * @return The data representing the new user.
 */
const createUser = async (
  username: string,
  password: string,
  profilePicture: string,
  role: string = 'user',
): Promise<User> => {
  const user: UserData = {
    username,
    password,
    profilePicture,
    role,
    followers: [],
    following: [],
    achievements: [],
    awards: [],
    score: 0,
    notifications: [],
    game: [],
    questions: [],
    answers: [],
    comments: [],
  };

  const res = await api.post(`${USER_API_URL}/createUser`, user);

  if (res.status === 409) {
    throw new Error('Username already exists');
  }

  if (res.status !== 200 || !res.data) {
    throw new Error('Error creating user');
  }

  return res.data;
};

/**
 * Function to fetch a user from the database by a specified username.
 * @param username - The username of the user to fetch.
 *
 * @return The data representing the user.
 */
const getUserByUsername = async (username: string) => {
  const res = await api.get(`${USER_API_URL}/getUserByUsername/${username}`);

  if (res.status !== 200 || !res.data) {
    throw new Error('Error fetching user');
  }

  return res.data;
};

/**
 * Function to fetch a user from the database by a specified uid.
 * @param uid - The uid of the user to fetch.
 *
 * @return The data representing the user.
 */
const getUserByUid = async (uid: string) => {
  const res = await api.get(`${USER_API_URL}/getUserByUid/${uid}`);

  if (res.status !== 200 || !res.data) {
    throw new Error('Error fetching user');
  }

  return res.data;
};

/**
 * Function to fetch all users from the database.
 *
 * @return The data representing all users.
 */
const getAllUsers = async () => {
  const res = await api.get(`${USER_API_URL}/getAllUsers`);

  if (res.status !== 200 || !res.data) {
    throw new Error('Error fetching user');
  }

  return res.data;
};

/**
 * Function to handle a user following or unfollowing another.
 * @param follower - User ID of the follower
 * @param following - User ID of the user being followed
 * @param action - The type of follow event taking place
 * @return - Updated User
 */
const followEvent = async (follower: string, following: string, action: 'follow' | 'unfollow') => {
  const res = await api.put(`${USER_API_URL}/followEvent`, {
    follower,
    following,
    action,
  });

  if (res.status !== 200) {
    throw new Error(`Error ${action}ing user`);
  }

  return res.data;
};

/**
 * Function to handle updating a game for both users playing.
 * @param going - User ID of the person sending the game event
 * @param waiting - User ID of the user being receiving the game event
 * @param data - The data representing the game event taking place
 * @param type - The type of game event taking place
 * @return - Updated User
 */
const gameEvent = async (
  going: string,
  waiting: string,
  data: (string | number)[],
  type: 'initiation' | 'turn' | 'quit' | 'exit',
) => {
  const res = await api.put(`${USER_API_URL}/gameEvent`, {
    going,
    waiting,
    data,
    type,
  });

  if (res.status !== 200) {
    throw new Error(`Error completing game event`);
  }

  return res.data;
};

/**
 * Function to update Achievement
 * @param uid - User ID
 * @param achievementName - Achievement Name
 * @returns - Updated User
 */
const addAchievement = async (uid: string, achievementName: string): Promise<User> => {
  const body: RewardEventRequestBody = {
    uid,
    reward: achievementName,
    type: 'achievement',
  };
  const res = await api.post(`${USER_API_URL}/updateAchievement`, body);

  if (res.status !== 200 || !res.data) {
    throw new Error('Error updating achievement');
  }

  return res.data;
};

/**
 * Function to update user by settings
 * @param user - User object
 * @returns - Updated User
 */
const updateUser = async (
  uid: string,
  field: 'username' | 'password' | 'profilePicture' | 'score',
  updatedValue: string,
): Promise<User> => {
  if (uid === '' || updatedValue === '') {
    throw new Error('Invalid UID');
  }

  const res = await api.put(`${USER_API_URL}/updateUser`, {
    uid,
    field,
    updatedValue,
  });

  if (res.status !== 200 || !res.data) {
    throw new Error('Error updating user');
  }

  return res.data;
};

/**
 * Function to delete a user
 */
const deleteUser = async (
  uid: string,
): Promise<{ acknowledged: boolean; deletedCount: number }> => {
  const res = await api.delete(`${USER_API_URL}/deleteUser/?uid=${uid}`);

  if (res.status !== 200 || !res.data) {
    throw new Error('Error deleting user');
  }

  return res.data;
};

export {
  addAward,
  loginUser,
  createUser,
  getUserByUsername,
  getUserByUid,
  getAllUsers,
  followEvent,
  gameEvent,
  addAchievement,
  updateUser,
  deleteUser,
};
